
export const STORAGE_KEYS = {
  PAGINATION_VALUE: "paginationValue",
  USER_PREFERENCES: "userPreferences",
  THEME: "theme",
  LANGUAGE: "language",
  FOLDER_MAPPINGS: "folderMappings",
  INTEGRATION_PAGINATION_VALUE: "integrationPaginationValue",
  INTEGRATION_FILTERS: "integrationFilters",
} as const;

export const DEFAULT_VALUES = {
  PAGINATION_VALUE: 25,
  THEME: "light",
  LANGUAGE: "en",
  INTEGRATION_PAGINATION_VALUE: 25,
  INTEGRATION_FILTERS: {
    nextRunFilter: "",
    lastRunFilter: "",
    search: "",
  },
} as const;

export interface UserPreferences {
  paginationValue: number;
  theme: "light" | "dark";
  language: string;
  integrationPaginationValue: number;
  integrationFilters: {
    nextRunFilter: string;
    lastRunFilter: string;
    search: string;
  };
}

export const localStorageUtils = {
  setItem: <T>(key: string, value: T): boolean => {
    try {
      const serializedValue = JSON.stringify(value);
      localStorage.setItem(key, serializedValue);
      return true;
    } catch (error) {
      return false;
    }
  },

  getItem: <T>(key: string, defaultValue: T): T => {
    try {
      const item = localStorage.getItem(key);
      if (item === null) {
        return defaultValue;
      }
      return JSON.parse(item) as T;
    } catch (error) {
      return defaultValue;
    }
  },

  removeItem: (key: string): boolean => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (error) {
      return false;
    }
  },

  clearAppData: (): boolean => {
    try {
      Object.values(STORAGE_KEYS).forEach((key) => {
        localStorage.removeItem(key);
      });
      return true;
    } catch (error) {
      return false;
    }
  },

  isAvailable: (): boolean => {
    try {
      const test = "__localStorage_test__";
      localStorage.setItem(test, test);
      localStorage.removeItem(test);
      return true;
    } catch {
      return false;
    }
  },
};

export const paginationStorage = {
  getPaginationValue: (): number => {
    return localStorageUtils.getItem<number>(
      STORAGE_KEYS.PAGINATION_VALUE,
      DEFAULT_VALUES.PAGINATION_VALUE,
    );
  },

  setPaginationValue: (value: number): boolean => {
    return localStorageUtils.setItem(STORAGE_KEYS.PAGINATION_VALUE, value);
  },

  setValidPaginationValue: (value: number, validValues: number[]): boolean => {
    if (validValues.includes(value)) {
      return paginationStorage.setPaginationValue(value);
    }
    return false;
  },

  clearPaginationData: (): boolean => {
    return localStorageUtils.removeItem(STORAGE_KEYS.PAGINATION_VALUE);
  },

  resetToDefault: (): boolean => {
    return paginationStorage.setPaginationValue(
      DEFAULT_VALUES.PAGINATION_VALUE,
    );
  },
};

export const userPreferencesStorage = {
  getUserPreferences: (): UserPreferences => {
    return localStorageUtils.getItem<UserPreferences>(
      STORAGE_KEYS.USER_PREFERENCES,
      {
        paginationValue: DEFAULT_VALUES.PAGINATION_VALUE,
        theme: DEFAULT_VALUES.THEME as "light" | "dark",
        language: DEFAULT_VALUES.LANGUAGE,
        integrationPaginationValue: DEFAULT_VALUES.INTEGRATION_PAGINATION_VALUE,
        integrationFilters: DEFAULT_VALUES.INTEGRATION_FILTERS,
      },
    );
  },

  setUserPreferences: (preferences: Partial<UserPreferences>): boolean => {
    const current = userPreferencesStorage.getUserPreferences();
    const updated = { ...current, ...preferences };
    return localStorageUtils.setItem(STORAGE_KEYS.USER_PREFERENCES, updated);
  },

  updatePreference: <K extends keyof UserPreferences>(
    key: K,
    value: UserPreferences[K],
  ): boolean => {
    const current = userPreferencesStorage.getUserPreferences();
    return userPreferencesStorage.setUserPreferences({ [key]: value });
  },
};

export const themeStorage = {
  getTheme: (): "light" | "dark" => {
    return localStorageUtils.getItem<"light" | "dark">(
      STORAGE_KEYS.THEME,
      DEFAULT_VALUES.THEME as "light" | "dark",
    );
  },

  setTheme: (theme: "light" | "dark"): boolean => {
    return localStorageUtils.setItem(STORAGE_KEYS.THEME, theme);
  },
};

export const languageStorage = {
  getLanguage: (): string => {
    return localStorageUtils.getItem<string>(
      STORAGE_KEYS.LANGUAGE,
      DEFAULT_VALUES.LANGUAGE,
    );
  },

  setLanguage: (language: string): boolean => {
    return localStorageUtils.setItem(STORAGE_KEYS.LANGUAGE, language);
  },
};

export const folderMappingsStorage = {
  getFolderMappings: (): Record<string, string> => {
    return localStorageUtils.getItem<Record<string, string>>(
      STORAGE_KEYS.FOLDER_MAPPINGS,
      {},
    );
  },

  setFolderMappings: (mappings: Record<string, string>): boolean => {
    return localStorageUtils.setItem(STORAGE_KEYS.FOLDER_MAPPINGS, mappings);
  },

  setFolderMapping: (fname: string, fsource: string): boolean => {
    const currentMappings = folderMappingsStorage.getFolderMappings();
    const updatedMappings = { ...currentMappings, [fname]: fsource };
    return folderMappingsStorage.setFolderMappings(updatedMappings);
  },

  getFsource: (fname: string): string | null => {
    const mappings = folderMappingsStorage.getFolderMappings();
    return mappings[fname] || null;
  },

  getFname: (fsource: string): string | null => {
    const mappings = folderMappingsStorage.getFolderMappings();
    for (const [fname, storedFsource] of Object.entries(mappings)) {
      if (storedFsource === fsource) {
        return fname;
      }
    }
    return null;
  },

  removeFolderMapping: (fname: string): boolean => {
    const currentMappings = folderMappingsStorage.getFolderMappings();
    const { [fname]: removed, ...updatedMappings } = currentMappings;
    return folderMappingsStorage.setFolderMappings(updatedMappings);
  },

  clearFolderMappings: (): boolean => {
    return localStorageUtils.removeItem(STORAGE_KEYS.FOLDER_MAPPINGS);
  },

  clearIrrelevantMappings: (currentPrefix: string): boolean => {
    const mappings = folderMappingsStorage.getFolderMappings();
    const currentPathParts = currentPrefix.split("/").filter((part) => part);

    const relevantMappings: Record<string, string> = {};

    for (const [fname, fsource] of Object.entries(mappings)) {
      const mappingPathParts = fsource.split("/").filter((part) => part);

      let isRelevant = false;

      if (currentPathParts.length === 0) {
        isRelevant = true;
      } else {
        const mappingPrefix = mappingPathParts
          .slice(0, currentPathParts.length)
          .join("/");
        const currentPrefixStr = currentPathParts.join("/");

        isRelevant =
          mappingPrefix === currentPrefixStr ||
          currentPrefixStr.startsWith(mappingPrefix + "/") ||
          mappingPrefix.startsWith(currentPrefixStr + "/");
      }

      if (isRelevant) {
        relevantMappings[fname] = fsource;
      }
    }

    return folderMappingsStorage.setFolderMappings(relevantMappings);
  },

  clearMappingsForHigherLevel: (newPrefix: string): boolean => {
    const mappings = folderMappingsStorage.getFolderMappings();
    const newPathParts = newPrefix.split("/").filter((part) => part);

    const relevantMappings: Record<string, string> = {};

    for (const [fname, fsource] of Object.entries(mappings)) {
      const mappingPathParts = fsource.split("/").filter((part) => part);

      const isRelevant = mappingPathParts.length <= newPathParts.length;

      if (isRelevant) {
        relevantMappings[fname] = fsource;
      }
    }

    return folderMappingsStorage.setFolderMappings(relevantMappings);
  },

  hasFolderMapping: (fname: string): boolean => {
    const mappings = folderMappingsStorage.getFolderMappings();
    return fname in mappings;
  },

  testClearingLogic: () => {
    folderMappingsStorage.setFolderMappings({
      paystubs: "netchex/data/paystubs",
      Netchex: "netchex/data",
      documents: "netchex/data/documents",
    });

    folderMappingsStorage.clearMappingsForHigherLevel("netchex/data");

    folderMappingsStorage.setFolderMappings({
      Netchex: "netchex/data",
      documents: "netchex/data/documents",
    });

    folderMappingsStorage.clearIrrelevantMappings("netchex/data/documents");
  },
};

export default {
  localStorageUtils,
  paginationStorage,
  userPreferencesStorage,
  themeStorage,
  languageStorage,
  folderMappingsStorage,
  STORAGE_KEYS,
  DEFAULT_VALUES,
};
